/**
 * Translation
 *
 * Loads a locale JSON, resolves dotted keys, interpolates, and falls back to
 * English for missing keys — visibly in development, silently in production.
 *
 * ⚠ **Sanskrit technical terms are never translated away, in any locale** (A5).
 * "nakṣatra" stays nakṣatra in English rather than becoming "lunar mansion", and
 * रāśi stays रāśi. The locale changes the *script and the surrounding prose*, not
 * the vocabulary of the śāstra — a reader who learns "the 7th bhāva" here can open
 * any classical text and find the same word.
 *
 * © Manish Jagdish Thatte
 */

import { DEFAULTS, LOCALES } from '../config.js';
import { applyNumerals } from './format.js';

const bundles = new Map();
let current = DEFAULTS.locale;
let fallback = null;

/** Missing keys seen this session, for the dev-time report. */
const missing = new Set();

/**
 * Listeners for "a locale bundle is now loaded".
 *
 * ⚠ **This is a different event from "the locale setting changed", and the
 * difference caused a real bug.** Components are upgraded as soon as their
 * module is imported, which is before `boot()` has awaited `loadLocale`. So the
 * first render of <site-nav> called `t('nav.home')` against an empty bundle and
 * painted the raw key. Subscribing to the *setting* did not help: the setting
 * had not changed, only the data behind it had arrived.
 *
 * ◆ So the signal belongs to the bundle, not to the preference. Anything that
 * renders translated text subscribes here.
 */
const localeListeners = new Set();

/** Subscribe to bundle loads. Returns an unsubscribe function. */
export function onLocaleChange(fn) {
  localeListeners.add(fn);
  return () => localeListeners.delete(fn);
}

function notifyLocaleLoaded(locale) {
  for (const fn of localeListeners) {
    try {
      fn(locale);
    } catch (err) {
      console.error('[i18n] locale listener threw:', err);
    }
  }
}

const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

/**
 * Feature bundles: `src/i18n/parts/<area>.<locale>.json` (e.g. `palm.en.json`).
 *
 * ◆ Each feature owns its own strings in its own file, deep-merged over the base
 * `<locale>.json` at load. That keeps several people from editing one large JSON
 * at once, and lets a locale fall back to English *per area*: a `palm.hi.json`
 * that does not exist yet simply means the palm page shows English, while every
 * other page is already Hindi.
 */
const partLoaders = import.meta.glob('../i18n/parts/*.json');

function deepMerge(into, from) {
  for (const [k, v] of Object.entries(from ?? {})) {
    if (k.startsWith('_')) continue;
    if (v && typeof v === 'object' && !Array.isArray(v)) into[k] = deepMerge(into[k] && typeof into[k] === 'object' ? into[k] : {}, v);
    else into[k] = v;
  }
  return into;
}

async function loadBundle(id) {
  const base = await import(`../i18n/${id}.json`);
  const merged = deepMerge({}, base.default ?? base);
  merged._meta = (base.default ?? base)._meta;
  for (const [path, load] of Object.entries(partLoaders)) {
    if (!path.endsWith(`.${id}.json`)) continue;
    const mod = await load();
    deepMerge(merged, mod.default ?? mod);
  }
  return merged;
}

/** Load a locale bundle. Falls back to English if the locale has no file. */
export async function loadLocale(locale) {
  const id = LOCALES.includes(locale) ? locale : DEFAULTS.locale;
  if (!bundles.has(id)) bundles.set(id, await loadBundle(id));
  if (!fallback && id !== DEFAULTS.locale) fallback = bundles.get(DEFAULTS.locale) ?? await loadBundle(DEFAULTS.locale);
  current = id;
  notifyLocaleLoaded(id);
  return bundles.get(id);
}

export function currentLocale() {
  return current;
}

export function localeMeta(locale = current) {
  return bundles.get(locale)?._meta ?? { locale, dir: 'ltr', numerals: 'latin' };
}

/** Resolve a dotted path against an object. */
/**
 * Resolve a dotted path against an object.
 *
 * ⚠ **An empty string counts as missing, not as a deliberate blank.** Scaffolded
 * locale files are full of `""` placeholders, and treating those as real values
 * rendered the entire navigation blank in Hindi, Marathi and Sanskrit — a
 * failure that looks like a layout bug rather than a missing translation. A key
 * that is genuinely meant to render nothing should be absent from the bundle.
 */
function lookup(bundle, key) {
  let node = bundle;
  for (const part of String(key).split('.')) {
    if (node == null || typeof node !== 'object') return undefined;
    node = node[part];
  }
  return typeof node === 'string' && node.length > 0 ? node : undefined;
}

/**
 * Translate `key`, interpolating `{name}` placeholders from `vars`.
 *
 * ⚠ A missing key returns the **key itself**, never an empty string. An empty
 * string looks like a deliberately blank label and hides the omission; a visible
 * `nav.kundali` in the UI gets fixed.
 */
export function t(key, vars = {}) {
  const bundle = bundles.get(current) ?? {};
  let text = lookup(bundle, key);

  if (text === undefined && fallback) text = lookup(fallback, key);
  if (text === undefined) {
    missing.add(key);
    if (isDev) console.warn(`[i18n] missing key: ${key} (locale ${current})`);
    return key;
  }

  const filled = text.replace(/\{(\w+)\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{${name}}`);

  return filled;
}

/**
 * Translate and apply the locale's numeral system.
 *
 * ◆ Separate from `t` because not every string should have its digits converted —
 * a Julian Day or a raw longitude in a debug view stays in Latin digits, and
 * `formatOffset('+05:21:10')` in a Marathi UI should read ०५:२१:१० in the chart
 * but not in an exported machine-readable field.
 */
export function tn(key, vars = {}, numerals = DEFAULTS.numerals) {
  return applyNumerals(t(key, vars), current, numerals);
}

/** The keys that had no translation this session. Empty is the goal. */
export function missingKeys() {
  return [...missing].sort();
}

/**
 * A name object from the constants tables, in the current locale.
 *
 * ◆ The tables carry `{ en, sa, hi, mr }` directly, so rāśi and graha names do not
 * go through the i18n bundles at all — they are data, not copy, and duplicating
 * them into four JSON files would be four chances to mistranslate Vṛścika.
 */
export function localName(nameObj, locale = current) {
  if (!nameObj) return '';
  return nameObj[locale] ?? nameObj.sa ?? nameObj.en ?? '';
}

/** Set `lang` and `dir` on the document so the browser hyphenates and speaks correctly. */
export function applyDocumentLocale(locale = current) {
  if (typeof document === 'undefined') return;
  const meta = localeMeta(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = meta.dir ?? 'ltr';
}
